import { useState, useRef, useCallback, useMemo } from 'react';
import { format, subDays, isValid, parseISO } from 'date-fns';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Download,
  DollarSign,
  Calendar,
  TrendingUp,
  TrendingDown,
  Info,
  ArrowUpRight,
  ArrowDownRight,
  Filter,
  Activity,
  Wallet
} from 'lucide-react';
import { useCashFlowStatement, ViewPeriod, DisplayCurrency, CashFlowRow } from '@/hooks/finance/useCashFlowStatement';
import { formatCurrency } from '@/lib/currency';
import { cn } from '@/lib/utils';

import * as XLSX from 'xlsx';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell
} from 'recharts';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export default function CashFlowStatementPage() {
  const today = format(new Date(), 'yyyy-MM-dd');
  const thirtyDaysAgo = format(subDays(new Date(), 30), 'yyyy-MM-dd');

  const [dateFrom, setDateFrom] = useState(thirtyDaysAgo);
  const [dateTo, setDateTo] = useState(today);
  const [viewPeriod, setViewPeriod] = useState<ViewPeriod>('daily');
  const [displayCurrency, setDisplayCurrency] = useState<DisplayCurrency>('ETB');

  const { rows, periodLabels, periodKeys, isLoading } = useCashFlowStatement(dateFrom, dateTo, viewPeriod, displayCurrency);

  const formatValue = useCallback((val: number) => {
    if (val === 0) return '–';
    if (val < 0) return `(${formatCurrency(Math.abs(val), displayCurrency)})`;
    return formatCurrency(val, displayCurrency);
  }, [displayCurrency]);

  const handleExport = () => {
    const headers = ['Line Item', ...periodLabels.map(p => p.label)];
    const wsData = [headers];
    for (const row of rows) {
      const prefix = row.indent ? '  '.repeat(row.indent) : '';
      const label = prefix + row.label;
      const vals = periodLabels.map(p => {
        const v = row.values.get(p.key) || 0;
        return row.isHeader ? '' : Number(v.toFixed(2));
      });
      wsData.push([label, ...vals as any]);
    }
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cash Flow');
    XLSX.writeFile(wb, `cash-flow-${dateFrom}-to-${dateTo}-${displayCurrency}.xlsx`);
  };

  // KPI Calculations
  const metrics = useMemo(() => {
    if (!rows.length || !periodKeys.length) return null;

    const closingRow = rows.find(r => r.key === 'closing');
    const netCashRow = rows.find(r => r.key === 'net-cash-flow');
    const salesRow = rows.find(r => r.key === 'total-sales');
    const opCashRow = rows.find(r => r.key === 'net-operating');

    const lastPeriodKey = periodKeys[periodKeys.length - 1];

    const closingBalance = closingRow?.values.get(lastPeriodKey) || 0;

    let totalNetCash = 0;
    periodKeys.forEach(k => { totalNetCash += netCashRow?.values.get(k) || 0; });

    let totalSales = 0;
    periodKeys.forEach(k => { totalSales += salesRow?.values.get(k) || 0; });

    let totalOpCash = 0;
    periodKeys.forEach(k => { totalOpCash += opCashRow?.values.get(k) || 0; });

    const efficiency = totalSales > 0 ? (totalOpCash / totalSales) * 100 : 0;

    return { closingBalance, totalNetCash, totalSales, totalOpCash, efficiency };
  }, [rows, periodKeys]);

  // Chart Data preparation
  const chartData = useMemo(() => {
    if (!periodKeys.length || !rows.length) return [];

    const inflows = rows.find(r => r.key === 'total-sales');
    const netFlow = rows.find(r => r.key === 'net-cash-flow');

    return periodKeys.map(k => {
      const label = periodLabels.find(pl => pl.key === k)?.label || k;
      return {
        name: label,
        inflow: inflows?.values.get(k) || 0,
        netFlow: netFlow?.values.get(k) || 0,
      };
    });
  }, [periodKeys, rows, periodLabels]);

  return (
    <Layout>
      <div className="space-y-6 pb-12 animate-in fade-in duration-700">

        {/* Modern Header Section */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="h-8 w-1 bg-primary rounded-full" />
              <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">Cash Intelligence</h1>
            </div>
            <p className="text-muted-foreground font-medium flex items-center gap-2">
              <Badge variant="secondary" className="bg-slate-100 hover:bg-slate-200 text-slate-600 border-none px-2 py-0.5 text-[10px] uppercase font-bold tracking-wider">IAS 7 Pro</Badge>
              Analysis for {isValid(parseISO(dateFrom)) ? format(parseISO(dateFrom), 'MMM d') : '—'} — {isValid(parseISO(dateTo)) ? format(parseISO(dateTo), 'MMM d, yyyy') : '—'}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Filter Group Container */}
            <div className="flex items-center gap-1.5 p-1 bg-slate-50 border rounded-lg shadow-sm">
              <div className="flex items-center gap-2 px-2 border-r pr-3">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" className="h-8 px-2 text-xs font-semibold gap-1.5 hover:bg-white">
                      <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                      {isValid(parseISO(dateFrom)) ? format(parseISO(dateFrom), 'MMM d, yyyy') : 'Start date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={isValid(parseISO(dateFrom)) ? parseISO(dateFrom) : undefined}
                      onSelect={(date) => date && setDateFrom(format(date, 'yyyy-MM-dd'))}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                    />
                  </PopoverContent>
                </Popover>
                <span className="text-muted-foreground">—</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" className="h-8 px-2 text-xs font-semibold gap-1.5 hover:bg-white">
                      <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                      {isValid(parseISO(dateTo)) ? format(parseISO(dateTo), 'MMM d, yyyy') : 'End date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={isValid(parseISO(dateTo)) ? parseISO(dateTo) : undefined}
                      onSelect={(date) => date && setDateTo(format(date, 'yyyy-MM-dd'))}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <Select value={viewPeriod} onValueChange={v => setViewPeriod(v as ViewPeriod)}>
                <SelectTrigger className="w-28 h-8 border-none bg-transparent hover:bg-white shadow-none text-xs font-semibold">
                  <Activity className="h-3.5 w-3.5 mr-2 text-slate-400" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>

              <div className="h-5 w-px bg-slate-200 mx-1" />

              <div className="flex p-0.5 gap-0.5">
                {(['ETB', 'USD'] as const).map(c => (
                  <button
                    key={c}
                    className={cn(
                      'px-3 py-1 rounded-md text-[10px] font-bold transition-all',
                      displayCurrency === c
                        ? 'bg-white text-primary shadow-sm ring-1 ring-slate-200'
                        : 'text-slate-500 hover:text-slate-900'
                    )}
                    onClick={() => setDisplayCurrency(c)}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            <Button onClick={handleExport} variant="outline" size="sm" className="h-10 px-4 gap-2 rounded-lg font-bold border-slate-200 shadow-sm hover:bg-slate-50">
              <Download className="h-4 w-4" />
              Export
            </Button>
          </div>
        </div>

        {/* Intelligence Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card className="relative overflow-hidden border-none shadow-md bg-gradient-to-br from-slate-900 to-slate-800 text-white">
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <Wallet className="h-12 w-12" />
            </div>
            <CardHeader className="pb-2 space-y-0">
              <CardDescription className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Total Cash Position</CardDescription>
              <CardTitle className="text-2xl font-black">{formatCurrency(metrics?.closingBalance || 0, displayCurrency)}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-1.5 mt-2">
                {(metrics?.totalNetCash || 0) >= 0 ? (
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-none hover:bg-emerald-500/30 font-bold px-1.5 py-0 text-[10px]">
                    <ArrowUpRight className="h-3 w-3 mr-0.5" />
                    +{formatPercent((metrics?.totalNetCash || 0) / (metrics?.closingBalance || 1))}
                  </Badge>
                ) : (
                  <Badge className="bg-rose-500/20 text-rose-400 border-none hover:bg-rose-500/30 font-bold px-1.5 py-0 text-[10px]">
                    <ArrowDownRight className="h-3 w-3 mr-0.5" />
                    {formatPercent((metrics?.totalNetCash || 0) / (metrics?.closingBalance || 1))}
                  </Badge>
                )}
                <span className="text-[10px] text-slate-400 font-medium">vs start of period</span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-sm bg-white/50 backdrop-blur-sm border-l-4 border-l-emerald-500">
            <CardHeader className="pb-2 space-y-0">
              <CardDescription className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Period Net Flow</CardDescription>
              <CardTitle className={cn(
                "text-2xl font-black",
                (metrics?.totalNetCash || 0) >= 0 ? "text-emerald-600" : "text-rose-600"
              )}>
                {metrics?.totalNetCash && metrics.totalNetCash < 0 ? `(${formatCurrency(Math.abs(metrics.totalNetCash), displayCurrency)})` : formatCurrency(metrics?.totalNetCash || 0, displayCurrency)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[10px] text-slate-400 font-medium">Consolidated Liquidity Delta</p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-sm bg-white/50 backdrop-blur-sm">
            <CardHeader className="pb-2 space-y-0 text-center">
              <CardDescription className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Op. Cash Efficiency</CardDescription>
              <CardTitle className="text-3xl font-black text-slate-800">{metrics?.efficiency.toFixed(1)}%</CardTitle>
            </CardHeader>
            <CardContent className="flex justify-center pt-2">
              <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden max-w-[120px]">
                <div
                  className={cn(
                    "h-full transition-all duration-1000",
                    (metrics?.efficiency || 0) > 20 ? "bg-emerald-500" : "bg-amber-500"
                  )}
                  style={{ width: `${Math.min(Math.max(metrics?.efficiency || 0, 0), 100)}%` }}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-sm bg-white/50 backdrop-blur-sm border-l-4 border-l-blue-500">
            <CardHeader className="pb-2 space-y-0">
              <CardDescription className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Op. Cash Generated</CardDescription>
              <CardTitle className="text-2xl font-black text-blue-600">
                {formatCurrency(metrics?.totalOpCash || 0, displayCurrency)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[10px] text-slate-400 font-medium">Core business cash production</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Analytics + Grid Layout */}
        <div className="grid gap-6 lg:grid-cols-3">

          {/* Trend Analytics Chart */}
          <Card className="lg:col-span-2 border-none shadow-md overflow-hidden bg-white">
            <CardHeader className="pb-0 border-b bg-slate-50/50">
              <div className="flex items-center justify-between py-2">
                <div>
                  <CardTitle className="text-sm font-bold flex items-center gap-2">
                    Cash Flow Pulse
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger><Info className="h-3 w-3 text-slate-400" /></TooltipTrigger>
                        <TooltipContent>Net Cash Flow (Area) vs Inflows (Bar)</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </CardTitle>
                  <CardDescription className="text-[10px]">Temporal liquidity variance</CardDescription>
                </div>
                <div className="flex gap-4 items-center">
                  <div className="flex items-center gap-1.5">
                    <div className="h-2 w-2 rounded-full bg-slate-200" />
                    <span className="text-[10px] font-bold text-slate-500">INFLOW</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="h-2 w-2 rounded-full bg-primary" />
                    <span className="text-[10px] font-bold text-slate-500">NET FLOW</span>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-6 h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorNet" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.1} />
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 9, fontWeight: 600, fill: '#64748B' }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 9, fontWeight: 600, fill: '#64748B' }}
                    tickFormatter={(val) => val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val}
                  />
                  <RechartsTooltip
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '10px' }}
                    labelStyle={{ fontWeight: 800, marginBottom: '4px' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="netFlow"
                    stroke="hsl(var(--primary))"
                    strokeWidth={3}
                    fillOpacity={1}
                    fill="url(#colorNet)"
                  />
                  <Area
                    type="monotone"
                    dataKey="inflow"
                    stroke="#CBD5E1"
                    strokeWidth={1}
                    fill="#F1F5F9"
                    fillOpacity={0.4}
                    strokeDasharray="5 5"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Quick Category Breakdown */}
          <Card className="border-none shadow-md overflow-hidden bg-white">
            <CardHeader className="pb-2 border-b bg-slate-50/50">
              <CardTitle className="text-sm font-bold leading-none">Activity Weight</CardTitle>
              <CardDescription className="text-[10px]">Net impact by activity type</CardDescription>
            </CardHeader>
            <CardContent className="p-4 flex flex-col items-center justify-center h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[
                  { name: 'Operating', val: metrics?.totalOpCash || 0 },
                  { name: 'Investing', val: Math.abs(metrics?.totalNetCash ? rows.find(r => r.key === 'net-investing')?.values.get(periodKeys[0]) || 0 : 0) }, // Simplified for visualization
                  { name: 'Financing', val: Math.abs(metrics?.totalNetCash ? rows.find(r => r.key === 'net-financing')?.values.get(periodKeys[0]) || 0 : 0) }
                ]} layout="vertical" margin={{ left: 0, right: 30 }}>
                  <XAxis type="number" hide />
                  <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 700 }} width={70} />
                  <RechartsTooltip cursor={{ fill: 'transparent' }} contentStyle={{ fontSize: '10px', borderRadius: '8px' }} />
                  <Bar dataKey="val" radius={[0, 4, 4, 0]} barSize={20}>
                    <Cell fill="#10B981" />
                    <Cell fill="#3B82F6" />
                    <Cell fill="#F59E0B" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Detailed Financial Grid */}
          <Card className="lg:col-span-3 border-none shadow-lg overflow-hidden bg-white">
            <CardHeader className="bg-slate-900 border-none pb-4">
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-white text-base">Consolidated Statement</CardTitle>
                  <CardDescription className="text-slate-400 text-[10px]">Indented ledger by GAAP classification</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[9px] uppercase font-black tracking-widest">Balanced</Badge>
                  <div className="px-2 py-1 rounded bg-slate-800 text-slate-300 text-[9px] font-mono select-all">REF: CF-{format(new Date(), 'yyMMdd')}</div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="flex items-center justify-center py-32 bg-slate-50/20">
                  <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="sticky left-0 z-20 bg-slate-50 w-64 px-4 py-3 text-left text-[10px] font-black text-slate-500 uppercase tracking-widest border-r border-slate-200">Line Item</th>
                        {periodLabels.map(p => (
                          <th key={p.key} className="px-4 py-3 text-right text-[10px] font-black text-slate-500 uppercase tracking-widest min-w-[120px]">
                            {p.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {rows.map(row => (
                        <StatementRow
                          key={row.key}
                          row={row}
                          periodLabels={periodLabels}
                          formatValue={formatValue}
                          periodKeys={periodKeys}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}

/* ─── Refined Statement Components ─── */

function StatementRow({
  row,
  periodLabels,
  formatValue,
  periodKeys
}: {
  row: CashFlowRow;
  periodLabels: { key: string; label: string }[];
  formatValue: (val: number) => string;
  periodKeys: string[];
}) {
  const isHeader = row.isHeader;
  const isTotal = row.isTotal;
  const isGrandTotal = row.key === 'net-cash-flow' || row.key === 'closing';

  if (isHeader) {
    return (
      <tr className="bg-slate-50/80 border-t-2 border-b border-slate-200/60">
        <td className="sticky left-0 z-10 bg-inherit px-4 py-2 border-r border-slate-200/60">
          <span className="text-[10px] font-black text-slate-900 uppercase tracking-widest">{row.label}</span>
        </td>
        {periodLabels.map(p => (
          <td key={p.key} className="py-2 px-4" />
        ))}
      </tr>
    );
  }

  return (
    <tr className={cn(
      "group transition-colors",
      isTotal ? (isGrandTotal ? "bg-slate-900 text-white hover:bg-slate-800" : "bg-slate-50 hover:bg-slate-100") : "hover:bg-slate-50/50"
    )}>
      <td className={cn(
        "sticky left-0 z-10 px-4 py-2 border-r transition-colors shadow-[4px_0_8px_-4px_rgba(0,0,0,0.05)]",
        isGrandTotal ? "bg-slate-900 border-slate-700" : "bg-white group-hover:bg-slate-50 border-slate-100"
      )}>
        <div
          className={cn(
            "text-[11px] truncate flex items-center justify-between",
            isTotal ? "font-bold text-slate-900" : "text-slate-600 font-medium",
            isGrandTotal && "text-white"
          )}
          style={{ paddingLeft: row.indent ? `${row.indent * 12}px` : undefined }}
        >
          {row.label}
          {!isTotal && !isHeader && (
            <div className="h-4 w-12 opacity-0 group-hover:opacity-100 transition-opacity">
              <RowSparkline data={periodKeys.map(k => row.values.get(k) || 0)} color={row.key.includes('sales') ? '#10B981' : '#94A3B8'} />
            </div>
          )}
        </div>
      </td>
      {periodLabels.map(p => {
        const val = row.values.get(p.key) || 0;
        return (
          <td
            key={p.key}
            className={cn(
              "px-4 py-2 text-right tabular-nums text-[11px] font-medium",
              isTotal && "font-black",
              isGrandTotal && "text-white py-3",
              !isTotal && val < 0 && "text-rose-500",
              !isTotal && val > 0 && "text-emerald-600",
              val === 0 && !isTotal && "text-slate-300 font-normal"
            )}
          >
            {formatValue(val)}
          </td>
        );
      })}
    </tr>
  );
}

function RowSparkline({ data, color }: { data: number[], color: string }) {
  // Ultra mini chart for row-level visualization
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min;

  return (
    <div className="flex items-end h-full gap-0.5 pt-1">
      {data.slice(-7).map((v, i) => (
        <div
          key={i}
          className="w-1 rounded-t-[1px]"
          style={{
            height: `${Math.max(((v - min) / range) * 100, 10)}%`,
            backgroundColor: color,
            opacity: 0.5 + (i * 0.08)
          }}
        />
      ))}
    </div>
  );
}

function formatPercent(val: number) {
  if (isNaN(val) || !isFinite(val)) return '0%';
  return `${(val * 100).toFixed(1)}%`;
}
